import React from "react";
import { useLocalStore, useObserver } from "mobx-react";
import { makeStyles } from "@material-ui/core/styles";
import { Form, FormxDebugger } from "@common/formx";
import locale from "@bonliva-crm/locale";
import { ErrorCard, Spinner, SpinnerOverlay, WithTooltip } from "@components";
import { Box, Button, ButtonProps, Grid, Typography } from "@material-ui/core";
import { HealthDeclarationQualityItemEditorStore } from "./health-declaration-quality-item-editor-store";
import { DatePicker, FileUpload, TextField, useSnackbarsPipeline } from "@bonliva-crm/ui";

type Props = {
  consultantId: string;
  country: string;
  itemName: string;
  itemType: string;
  onClose?: () => any;
};

export const HealthDeclarationQualityItemEditor: React.FC<Props> = (props) => {
  const { onClose } = props;
  const snackbar = useSnackbarsPipeline();

  const localStore = useLocalStore(
    () =>
      new HealthDeclarationQualityItemEditorStore(
        props.consultantId,
        props.country,
        props.itemName,
        props.onClose,
        snackbar
      )
  );

  const classes = useStyles({});

  return useObserver(() => {
    const { loading, data, form } = localStore;

    let actionButtons: (ButtonProps & {
      label: string;
      value: string;
      tooltipTitle?: string;
    })[] = [];

    if (data && data.AvailableStatuses) {
      actionButtons = [
        {
          label: locale.qualityStatuses.RequestReceivedNotProcessed,
          value: "RequestReceivedNotProcessed",
          onClick: () =>
            localStore.submitTransition("ManuallyHandle_RequestReceivedNotProcessed")
        },
        {
          label: locale.qualityStatuses.ApprovedWithComment,
          value: "ApprovedWithComment",
          onClick: () => localStore.submitTransition("ManuallyHandle_ApprovedWithComment")
        },
        {
          variant: "contained" as const,
          label: locale.qualityStatuses.Approved,
          value: "Approved",
          onClick: () => localStore.submitTransition("ManuallyHandle_Approved")
        }
      ]
        .filter((x) => localStore.availableStatusNames.includes(x.value))
        .map((option) => ({
          ...option,
          disabled: !form.valid || localStore.checkStatus(option.value).disabled
        }));
    }

    if (loading && !data) {
      return (
        <div className={classes.root}>
          <Spinner />
        </div>
      );
    }

    if (localStore.error) {
      return (
        <ErrorCard
          message={localStore.error.toString()}
          title={`Cannot load quality item`}
        />
      );
    }

    return (
      <div className={classes.root} data-test="QualityCheckEditor">
        <SpinnerOverlay
          show={loading}
          style={{ position: "absolute", width: "100%", height: "100%" }}
        />

        <div className={classes.wrapper}>
          <Typography variant="h3" display="block">
            {localStore.name}
          </Typography>

          <div className={classes.form}>
            <Form store={localStore.form} allowUpdates>
              <FormxDebugger store={localStore.form} />

              <DatePicker
                value={localStore.form.values.date}
                onChange={(value) => localStore.form.setValue("date", value)}
                label={locale.Date}
                DatePickerProps={{
                  name: "date",
                  disabled: localStore.isDateDisabled
                }}
              />

              <Box flex>
                <TextField
                  label={locale.Comment}
                  value={localStore.metadata.comment}
                  fullWidth
                  isClearable
                  onClear={() =>
                    localStore.setStatusMetaData({
                      ...localStore.metadata,
                      comment: ""
                    })
                  }
                  onChange={(e) =>
                    localStore.setStatusMetaData({
                      ...localStore.metadata,
                      comment: e.target.value
                    })
                  }
                />
              </Box>

              {localStore.withDocument && (
                <FileUpload
                  onFileRemove={() => {
                    localStore.fileToUpload = null;
                  }}
                  onFileUpload={(file) => {
                    localStore.fileToUpload = file;

                    localStore.handleUpload();
                  }}
                />
              )}
            </Form>
          </div>

          <div className={classes.controls}>
            <Button
              variant="text"
              size="small"
              color="primary"
              onClick={onClose}
              style={{ marginRight: 30 }}
            >
              {locale.Cancel}
            </Button>

            <Grid container spacing={1} justifyContent="flex-end">
              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!localStore.canCancel}
                  onClick={localStore.clearStatus}
                >
                  {locale.ClearStatus}
                </Button>
              </Grid>

              {actionButtons.map((option) => (
                <Grid item>
                  <WithTooltip title={option.tooltipTitle} key={option.value}>
                    <Button
                      key={option.value}
                      disabled={option.disabled}
                      variant={option.variant || "outlined"}
                      color="primary"
                      size="small"
                      onClick={option.onClick}
                    >
                      {option.label}
                    </Button>
                  </WithTooltip>
                </Grid>
              ))}
            </Grid>
          </div>
        </div>
      </div>
    );
  });
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    minWidth: 700,
    minHeight: 500
  },

  wrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: 36,
    width: "100%"
  },

  spinner: {
    position: "absolute",
    width: "100%",
    height: "100%"
  },

  overlay: {
    borderRadius: theme.shape.borderRadius,
    marginBottom: "0 !important"
  },

  label: {
    textAlign: "center"
  },

  form: {
    ...theme.mixins.childrenMarginBottom(theme.spacing(2)),
    marginTop: 32,
    flex: 1
  },

  controls: {
    ...theme.mixins.childrenMarginRight(theme.spacing(1)),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingTop: 40,
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));
